import { type HTMLAttributes, useEffect, useState } from 'react';

import type { ClassValue } from 'clsx';

import { cn } from '../../lib/utils';

const SHOW_DELAY_MS = 300;
const HIDE_DELAY_MS = 200;

export interface TopProgressBarProps {
    isActive: boolean;
    className?: ClassValue;
    dataTestid?: string;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
}

export const TopProgressBar = ({
    isActive,
    className,
    dataTestid = 'TopProgressBar',
    htmlAttributes,
}: TopProgressBarProps) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => setIsVisible(isActive), isActive ? SHOW_DELAY_MS : HIDE_DELAY_MS);
        return () => clearTimeout(timeout);
    }, [isActive]);

    return (
        <div
            className={cn(
                'pointer-events-none fixed inset-x-0 top-0 z-loadingbar h-0.75 overflow-hidden transition-opacity duration-200',
                isVisible ? 'opacity-100' : 'opacity-0',
                className
            )}
            data-testid={dataTestid}
            data-component-name='TopProgressBar'
            role='progressbar'
            aria-busy={isVisible}
            aria-hidden={!isVisible}
            {...htmlAttributes}
        >
            <div
                className={cn(
                    'h-full w-1/3 animate-indeterminate-progress bg-button-background motion-reduce:w-full motion-reduce:animate-none',
                    !isVisible && 'paused'
                )}
            />
        </div>
    );
};
